import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, tap } from 'rxjs';
import { LoginResponse, PermissaoMenuManutencaoResponse, PermissaoMenuRequest, Usuario, Permissao, UsuarioSeguranca, UsuarioAdminCreate } from '../models/auth.model';
import { SessionStorageService } from './session-storage.service';
import { ConfigService } from '../core/config/config.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {



  private readonly configService = inject(ConfigService);
  private userSubject = new BehaviorSubject<Usuario | null>(null);
  private userPermissions = new BehaviorSubject<Permissao[] | null>(null);

  user$ = this.userSubject.asObservable();
  permissoes$ = this.userPermissions.asObservable();

  constructor(private http: HttpClient, private sessionStorageService: SessionStorageService) {
    this.checkSession();
  }

  private get loginUrl(): string {
      const baseUrl = this.configService.settings()?.apiUrl;
      return `${baseUrl}/seguranca/login`;
    }

  private get segurancaUrl(): string {
    const baseUrl = this.configService.settings()?.apiUrl;
    return `${baseUrl}/seguranca`;
  }

  login(credentials: { nomeUsuario: string, senha: string, tenant?: string }): Observable<LoginResponse> {
    const body: { nomeUsuario: string; senha: string; tenant?: string } = {
      nomeUsuario: credentials.nomeUsuario,
      senha: credentials.senha
    };
    if (credentials.tenant) {
      body.tenant = credentials.tenant;
    }
    return this.http.post<LoginResponse>(this.loginUrl, body).pipe(
      tap(response => {
        this.userSubject.next(response.usuario);
        this.userPermissions.next(response.permissoes);
        this.sessionStorageService.saveLoginResponse(response);
      })
    );
  }

  getUser(): Usuario | null {
    return this.userSubject.getValue();
  }

  updateUsuario(id: number, payload: Usuario): Observable<Usuario> {
    return this.http.put<Usuario>(`${this.segurancaUrl}/usuarios/${id}`, payload).pipe(
      tap((usuarioAtualizado) => {
        this.userSubject.next(usuarioAtualizado);

        const loginResponse = this.sessionStorageService.getLoginResponse();
        if (loginResponse) {
          this.sessionStorageService.saveLoginResponse({
            ...loginResponse,
            usuario: usuarioAtualizado
          });
        }
      })
    );
  }

 permissoes(): Permissao[] | null {
    return this.userPermissions.getValue();
  }

  getPermissoesPorSlug(texto: string): Permissao[] {
    const termo = (texto ?? '').trim().toLowerCase();
    const permissoes = this.userPermissions.getValue() ?? [];
    if (!termo) {
      return permissoes;
    }

    return permissoes.filter(permissao =>
      (permissao.slug ?? '').toLowerCase().includes(termo)
    );
  }

  logout() {
    this.userSubject.next(null);
    this.userPermissions.next(null);
    this.sessionStorageService.clearSession();
  }

  private checkSession() {
    const loginResponse = this.sessionStorageService.getLoginResponse();
    if (loginResponse) {
      this.userSubject.next(loginResponse.usuario);
      this.userPermissions.next(loginResponse.permissoes);
    }
  }

  getUsuarios(): Observable<UsuarioSeguranca[]> {
    return this.http.get<UsuarioSeguranca[]>(`${this.segurancaUrl}/usuarios`);
  }

  createUsuario(payload: UsuarioAdminCreate): Observable<UsuarioAdminCreate> {
    return this.http.post<UsuarioAdminCreate>(`${this.segurancaUrl}/usuarios`, payload);
  }

  updateUsuarioAdmin(id: number, payload: UsuarioAdminCreate): Observable<UsuarioAdminCreate> {
    return this.http.put<UsuarioAdminCreate>(`${this.segurancaUrl}/usuarios/${id}`, payload);
  }

  deleteUsuario(id: number): Observable<void> {
    return this.http.delete<void>(`${this.segurancaUrl}/usuarios/${id}`);
  }

  getPermissoesManutencao(usuarioId: number): Observable<PermissaoMenuManutencaoResponse> {
    return this.http.get<PermissaoMenuManutencaoResponse>(`${this.segurancaUrl}/permissoes-menu/manutencao/${usuarioId}`);
  }

  createPermissaoMenu(payload: PermissaoMenuRequest): Observable<PermissaoMenuRequest> {
    return this.http.post<PermissaoMenuRequest>(`${this.segurancaUrl}/permissoes-menu`, payload);
  }

  updatePermissaoMenu(usuarioId: number, menuItemId: number, payload: PermissaoMenuRequest): Observable<PermissaoMenuRequest> {
    return this.http.put<PermissaoMenuRequest>(`${this.segurancaUrl}/permissoes-menu/${usuarioId}/${menuItemId}`, payload);
  }
}
